package com.advaita.cloud.drive.ui.components

import androidx.compose.runtime.Composable
import com.advaita.cloud.drive.ui.store.PreviewTooltipStore
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Pre
import org.jetbrains.compose.web.dom.Text

/** Width the tooltip is laid out for; mirrors `.preview-peek` in index.html. */
private const val PEEK_WIDTH_PX = 520.0

/** Narrowest box still worth drawing. Below this the padded columns are useless. */
private const val MIN_PEEK_WIDTH_PX = 240.0

/** Keep the box off the viewport edges. */
private const val MARGIN_PX = 12.0

/**
 * Hover peek for the workflow picker's magnifier (ADV-1220).
 *
 * Rendered at the top level rather than inside the dialog: `.modal` animates
 * `transform: scale()`, which would make it the containing block for a
 * position:fixed child, and the tooltip has to be able to sit outside the
 * dialog's bounds anyway.
 *
 * Placed to the LEFT of the icon, because the magnifier sits on the right-hand
 * side of the picker's rows and a right-hand tooltip would immediately run off
 * the dialog. Falls back to the right when there is no room on the left, and is
 * clamped on both axes so neither a row near the bottom nor a narrow window
 * pushes it off screen.
 *
 * Deliberately not scrollable and offers no controls: it is a peek at the head
 * of a file, chosen over a docked panel for the picker. The file view keeps the
 * full pane.
 */
@Composable
fun PreviewTooltip(store: PreviewTooltipStore) {
    val file = store.target ?: return
    val anchor = store.anchor ?: return
    val body = store.body
    val note = store.note

    // Horizontal and vertical are clamped the same way. The width shrinks first,
    // because a viewport narrower than the laid-out box has no position that
    // keeps a 520 px tooltip on screen. The inline width overrides the class.
    val width = PEEK_WIDTH_PX
        .coerceAtMost((window.innerWidth - MARGIN_PX * 2).coerceAtLeast(MIN_PEEK_WIDTH_PX))
    val placeLeft = anchor.left >= width + MARGIN_PX * 2
    val left = (if (placeLeft) anchor.left - width - MARGIN_PX else anchor.left + MARGIN_PX * 2)
        .coerceIn(MARGIN_PX, (window.innerWidth - width - MARGIN_PX).coerceAtLeast(MARGIN_PX))
    // Rough height estimate is enough: the clamp only has to keep the box on screen.
    val estimatedHeight = 26.0 + (body?.count { it == '\n' }?.plus(1) ?: 1) * 17.0
    val top = (anchor.top + anchor.height / 2 - estimatedHeight / 2)
        .coerceIn(MARGIN_PX, (window.innerHeight - estimatedHeight - MARGIN_PX).coerceAtLeast(MARGIN_PX))

    Div(attrs = {
        classes("preview-peek")
        attr("data-testid", "preview-peek")
        attr("role", "tooltip")
        style {
            property("left", "${left}px")
            property("top", "${top}px")
            property("width", "${width}px")
        }
    }) {
        Div(attrs = { classes("preview-peek-name") }) { Text(file.displayName) }
        when {
            note != null -> Div(attrs = { classes("preview-peek-note") }) { Text(note) }
            body == null -> Div(attrs = { classes("preview-peek-note") }) { Text("Loading…") }
            body.isBlank() -> Div(attrs = { classes("preview-peek-note") }) { Text("No rows to show.") }
            // Compose Text() into a <pre> escapes, so nothing in a previewed file
            // is ever interpreted as markup.
            else -> Pre(attrs = {
                classes("preview-peek-body")
                attr("data-testid", "preview-peek-body")
            }) { Text(body) }
        }
    }
}
